import React, { useEffect, useRef, useState } from "react";
import Button from "../../atomic/Button";
import {
  DocumentArrowDownIcon,
  PencilSquareIcon,
  TrashIcon
} from "@heroicons/react/24/outline";
import { DialogOne } from "../../atomic/Dialog";
import PrivateComponent from "../PrivateComponent";
import EditDocumentModal from "./EditDocumentModal";
import { useDocument } from "./DocumentComponentContext";
import ButtonWithDropdown from "../../atomic/Button/ButtonWithDropdown";
import {
  DOC_SAMPLED_DECLINE,
  DOC_SAMPLED_NEGATIVE,
  DOC_SAMPLED_POSITIVE,
  DOC_SAMPLED_REFER,
  DOC_STATUS_NOT_VERIFIED,
  DOC_STATUS_SAMPLED,
  DOC_STATUS_SCREENED,
  DOC_STATUS_UNDER_REVIEW,
  isImage
} from "./DocumentCompFunctions";
import { useDocumentApiService } from "./ApiCalls/DocumentComponentApiService";
import { notify } from "../../../shared/context/AlertContext";
import { deleteDocs } from "src/pages/LoanAdditionalDocsTab/LoanAdditionalDocAPICalls";

const DocumentActions = ({
  isMFL_LAP,
  reloadParent,
  allowDownload
}: {
  isMFL_LAP: boolean;
  reloadParent?: () => void;
  allowDownload?: boolean;
}) => {
  const [showEditDocument, setShowEditDocument] = useState(false);
  const { updateDocument, document } = useDocument();
  const {
    onUpdateAdditionalKYCDoc,
    uploadDocument,
    updateAdditionalKYCDocumentMedia,
    updateCoApplicantKYCDocument,
    updateKYCDocument,
    updateCoApplicantDocReviewStatus
  } = useDocumentApiService();

  const fileInputRef: any = useRef(null);

  const [file, setFile] = useState<{
    preview: string;
    raw: null | Blob;
    type: string;
  }>({ preview: "", raw: null, type: "" });

  const [mediaID, setMediaID] = useState(null);
  const [showEditImage, setShowEditImage] = React.useState(false);

  const onDownloadDocument = async () => {
    const isBack = document?.isBackImage;
    const url = isBack
      ? document?.documentUrl?.backImagePath
      : document?.documentUrl?.frontImagePath;

    if (!url) return;

    let fileName = isBack
      ? document?.documentUrl?.backFileName
      : document?.documentUrl?.frontFileName;

    if (!fileName) {
      try {
        const urlObj = new URL(url);
        const pathParts = urlObj.pathname.split("/");
        fileName = pathParts[pathParts.length - 1];
      } catch (e) {
        const urlParts = url.split("/");
        fileName = urlParts[urlParts.length - 1] || "document";
      }
    }

    try {
      // Browsers often cache the `<img>` tag request (which doesn't request CORS headers).
      // We pass `cache: "no-store"` to forcefully re-fetch the asset with proper CORS headers.
      const response = await fetch(url, { cache: "no-store" });
      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);

      const link = window.document.createElement("a");
      link.href = blobUrl;
      link.download = fileName;
      window.document.body.appendChild(link);
      link.click();
      window.document.body.removeChild(link);
      URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error("Error downloading file:", error);

      window.open(url, "_blank");
    }
  };

  const deleteDocument = async () => {
    const { token, loanID, pageName, isLenderDashboard } = document;

    const isSucess: any = await deleteDocs(document?.docID, {
      token,
      loanID,
      pageName,
      isLenderPlatform: isLenderDashboard
    });

    if (isSucess) reloadParent?.();
  };

  const onEditDocument = () => setShowEditDocument(true);

  const onFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.currentTarget.files?.[0];
    if (selectedFile) {
      let preview = URL.createObjectURL(selectedFile);
      setFile({
        preview: preview,
        raw: selectedFile ?? null,
        type: selectedFile.type
      });

      const response: any = await uploadDocument({
        file: selectedFile,
        userID: document.loanDetails?.userID,
        mediaType: "Photo",
        pageName: document.pageName,
        token: document.token,
        isLenderPlatform: document.isLenderDashboard || false
      });

      if (response?.data?.mediaID) {
        setMediaID(response?.data?.mediaID);
        setShowEditImage(!showEditImage);
        updateDocument({ uploadedDocument: response?.data?.mediaID });
      }
    }
  };

  const renderDropdownButton = (isBack: boolean, isImage: boolean) => {
    const handleStatusUpdate = async (status: number) => {
      let response: any;

      if (isMFL_LAP) {
        response = await onUpdateAdditionalKYCDoc({
          documentID: document?.docID,
          status: status,
          loanApplicationID: document.loanID,
          token: document.token,
          pageName: document.pageName
        });
      } else {
        if (document.isCoApplicant) {
          response = updateCoApplicantDocReviewStatus({
            pageName: document.pageName,
            token: document.token,
            loanApplicationID: document.loanID,
            coApplicantKYCDetailsID: document.coApplicantKYCDetailsID,
            status: status
          });
        } else {
          response = await onUpdateAdditionalKYCDoc({
            documentID: document.docID,
            status: status,
            loanApplicationID: document.loanID,
            token: document.token,
            pageName: document.pageName
          });
        }
      }

      if (response.status) {
        notify({
          title: "Success",
          type: "success",
          text: "Document status updated successfully"
        });

        updateDocument({ reloadParent: true });
      }
    };

    let statusItems = [
      { label: "Screened", status: DOC_STATUS_SCREENED },
      { label: "Under Review", status: DOC_STATUS_UNDER_REVIEW },
      { label: "Sampled", status: DOC_STATUS_SAMPLED },
      { label: "Not Verified", status: DOC_STATUS_NOT_VERIFIED },
      { label: "Sampled Positive", status: DOC_SAMPLED_POSITIVE },
      { label: "Sampled Refer", status: DOC_SAMPLED_REFER },
      { label: "Sampled Negative", status: DOC_SAMPLED_NEGATIVE },
      { label: "Sampled Decline", status: DOC_SAMPLED_DECLINE }
    ];

    let menuItems = [
      {
        label: "Edit Document",
        onClick: () =>
          isImage ? setShowEditDocument(true) : uploadNewDocument()
      },
      ...statusItems.map(({ label, status }) => ({
        label,
        onClick: () => handleStatusUpdate(status)
      }))
    ];

    if (isMFL_LAP) {
      const result = Object.entries(
        document?.documentScreeningOptions || {}
      ).map(([status, label]) => ({
        label,
        onClick: () => handleStatusUpdate(Number(status))
      }));

      menuItems = result;
    }

    return menuItems?.length > 0 ? (
      <ButtonWithDropdown
        menuItems={menuItems}
        buttonText=""
        icon={
          <PencilSquareIcon
            className={`${document.isCompact ? "w-3.5 h-3.5" : "w-4 h-4"} text-indigo-600`}
          />
        }
        styles={{
          container: "!ml-0 mt-1",
          button: `hidden md:flex border border-indigo-600 rounded-md bg-white ${
            document.isCompact ? "!p-0.5" : "!p-1"
          }`
        }}
      />
    ) : null;
  };

  const handleSaveImage = async () => {
    if (!mediaID) return;

    let response = null;

    try {
      if (document.isAdditionalDocs) {
        response = await updateAdditionalKYCDocumentMedia({
          documentID: document.documentID,
          loanApplicationID: document.loanID,
          mediaIDs: [mediaID],
          token: document.token,
          pageName: document.pageName
        });
      } else if (document.isCoApplicant) {
        response = await updateCoApplicantKYCDocument({
          loanApplicationID: document.loanID,
          userID: document.loanDetails?.userID ?? "",
          coApplicantKYCDetailsID: document.coApplicantKYCDetailsID,
          isBack: document.isBackImage || false,
          mediaID,
          token: document.token,
          pageName: document.pageName
        });
      } else {
        response = await updateKYCDocument({
          changes: ["IMAGE UPDATED"],
          isBack: false,
          loanApplicationID: document.loanID,
          loanKYCDetailsID: document.loanKycDetailsID,
          mediaID,
          token: document.token,
          pageName: document.pageName,
          isLenderDashboard: document.isLenderDashboard || false
        });
      }

      if (response?.status) {
        updateDocument({ callSaveAction: false, uploadedDocument: "" });
        setFile({ preview: "", raw: null, type: "" });
      } else {
        throw new Error(response?.error || "Unknown error occurred");
      }
    } catch (error: any) {
      updateDocument({ callSaveAction: false });
      notify({
        title: "Error",
        text: error.message || "Unexpected error occurred"
      });
    }
  };

  function uploadNewDocument() {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  }
  useEffect(() => {
    if (document?.callSaveAction) {
      handleSaveImage();
    }
  }, [document?.callSaveAction]);

  return (
    <>
      <div
        id="actions"
        className={`absolute gap-1 flex right-2 ${document.isCompact ? "top-1 right-1" : "top-2"}`}
      >
        {allowDownload && (
          <PrivateComponent
            permissions={document?.permissions || {}}
            requiredPage={document?.pageName || ""}
            isLenderPlatform={document?.isLenderDashboard || false}
            requiredFunctions={["Download KYC Docs"]}
          >
            <Button
              outline
              id="downloadDocument"
              color="primary"
              onClick={onDownloadDocument}
              className={`rounded-md bg-white !border mt-1 ${document.isCompact ? "!p-0.5" : "!p-1"}`}
            >
              <DocumentArrowDownIcon
                className={`${document.isCompact ? "w-3.5 h-3.5" : "w-4 h-4"} text-indigo-600`}
              />
            </Button>
          </PrivateComponent>
        )}

        {!document.isAdditionalDocs && document?.showEditDocument ? (
          <Button
            outline
            id="editDocument"
            color="primary"
            onClick={
              isImage(document?.documentUrl?.frontImagePath || "")
                ? onEditDocument
                : uploadNewDocument
            }
            className={`rounded-md bg-white !border mt-1 ${document.isCompact ? "!p-0.5" : "!p-1"}`}
          >
            <PencilSquareIcon
              className={`${document.isCompact ? "w-3.5 h-3.5" : "w-4 h-4"} text-indigo-600`}
            />
          </Button>
        ) : (
          document?.showEditDocument &&
          renderDropdownButton(
            document?.isBackImage || false,
            isImage(document?.documentUrl?.frontImagePath || "")
          )
        )}

        {isMFL_LAP && document?.allowDelete && (
          <Button
            outline
            id="deleteDocument"
            color="primary"
            onClick={deleteDocument}
            className={`rounded-md bg-white !border mt-1 ${document.isCompact ? "!p-0.5" : "!p-1"}`}
          >
            <TrashIcon
              className={`${document.isCompact ? "w-3.5 h-3.5" : "w-4 h-4"} text-indigo-600`}
            />
          </Button>
        )}
      </div>

      <DialogOne
        open={showEditDocument}
        onCloseProp={() => {
          updateDocument({ showEditDocument: false, uploadedFile: "" });
        }}
        setOpen={setShowEditDocument}
        title="View Document Details"
        styles={{ panel: "max-w-[90vw] h-[90vh] md:m-0" }}
      >
        <EditDocumentModal />
      </DialogOne>

      <input
        onChange={onFileSelect}
        className="hidden"
        type="file"
        ref={fileInputRef}
        accept="image/jpeg,image/png,application/pdf"
        multiple={false}
      />
    </>
  );
};

export default DocumentActions;
